import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import { formatLakhs, formatNumber, formatDateShort } from "../lib/format";
import { toast } from "sonner";
import { Download, Filter, Search, X, Save, History, AlertTriangle, IndianRupee, Wine } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line, Cell } from "recharts";

const STATUS_OPTIONS = [
  { v: "available", label: "Available", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  { v: "reserved",  label: "Reserved",  cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { v: "sold",      label: "Sold",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { v: "blocked",   label: "Blocked",   cls: "bg-zinc-700/30 text-zinc-400 border-zinc-700" },
];

const PAYMENT_OPTIONS = [
  { v: "pending", label: "Pending", cls: "text-zinc-400 border-zinc-700" },
  { v: "partial", label: "Partial", cls: "text-amber-300 border-amber-500/40" },
  { v: "paid",    label: "Paid",    cls: "text-emerald-300 border-emerald-500/40" },
];

const CHART_COLORS = ["#22c55e", "#0ea5e9", "#f59e0b", "#a855f7", "#ef4444"];

function StatusBadge({ value }) {
  const s = STATUS_OPTIONS.find((x) => x.v === value) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function PaymentBadge({ value }) {
  const s = PAYMENT_OPTIONS.find((x) => x.v === value) || PAYMENT_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border ${s.cls}`}>
      {s.label}
    </span>
  );
}

// Convert paise/rupees to Lakhs for formatLakhs helper
const rupeesToLakhs = (r) => (Number(r || 0) / 100000);

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // current table being edited
  const [logEntries, setLogEntries] = useState([]);

  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSoldBy, setFilterSoldBy] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [searchQ, setSearchQ] = useState("");

  const reload = async () => {
    const [t, s, u] = await Promise.all([
      api.get("/tables"),
      api.get("/tables/stats"),
      api.get("/tables/users"),
    ]);
    setTables(t.data.tables || []);
    setStats(s.data);
    setUsers(u.data.users || []);
  };
  useEffect(() => { reload(); }, []);

  const loadLog = async (tableId) => {
    try {
      const { data } = await api.get(`/tables/${tableId}/log`);
      setLogEntries(data.log || []);
    } catch {
      setLogEntries([]);
    }
  };

  const openEdit = async (t) => {
    setEditing({ ...t });
    loadLog(t.id);
  };
  const closeEdit = () => { setEditing(null); setLogEntries([]); };

  const saveEdit = async () => {
    if (!editing) return;
    // Guard: confirm reverting sold -> available
    const original = tables.find((x) => x.id === editing.id);
    if (original?.status === "sold" && editing.status === "available") {
      if (!window.confirm("Move this table from SOLD back to AVAILABLE? Customer data will remain but it'll re-enter inventory.")) return;
    }
    try {
      const payload = {
        status: editing.status,
        payment_status: editing.payment_status,
        closing_price: editing.closing_price === "" || editing.closing_price == null ? null : Number(editing.closing_price),
        sold_by: editing.sold_by || "",
        customer_name: editing.customer_name || "",
        customer_phone: editing.customer_phone || "",
        customer_email: editing.customer_email || "",
        company_name: editing.company_name || "",
        bottles: Number(editing.bottles || 0),
        complimentary_bottles: Number(editing.complimentary_bottles || 0),
        amount_received: Number(editing.amount_received || 0),
        date_sold: editing.date_sold || null,
        payment_due_date: editing.payment_due_date || null,
        notes: editing.notes || "",
      };
      await api.patch(`/tables/${editing.id}`, payload);
      toast.success(`Table ${editing.table_number} updated`);
      closeEdit();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const downloadCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/tables/export.csv`;
    window.open(url, "_blank");
  };

  // Categories detected from data (future-proof)
  const categories = useMemo(() => Array.from(new Set(tables.map((t) => t.category))).sort(), [tables]);

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return tables.filter((t) => {
      if (filterCategory !== "all" && t.category !== filterCategory) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterSoldBy !== "all" && (t.sold_by || "") !== filterSoldBy) return false;
      if (filterPayment !== "all" && t.payment_status !== filterPayment) return false;
      if (q) {
        const blob = [t.table_number, t.customer_name, t.customer_phone, t.company_name, t.customer_email]
          .join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [tables, filterCategory, filterStatus, filterSoldBy, filterPayment, searchQ]);

  const salespeopleSet = useMemo(() => Array.from(new Set(tables.map((t) => t.sold_by).filter(Boolean))).sort(), [tables]);

  if (!stats) {
    return (
      <Layout>
        <PageHeader overline="VIP Tables" title="Table Sales" testId="tables-loading" />
        <div className="text-zinc-500 text-sm">Loading…</div>
      </Layout>
    );
  }

  const inv = stats.inventory;
  const rev = stats.revenue;

  return (
    <Layout>
      <PageHeader
        overline="VIP Tables · 20-table inventory · ₹71L rack"
        title="Table Sales"
        subtitle="Live CRM for the VIP table sales floor. Edit any table inline — inventory, revenue and audit log update in real time."
        actions={
          <button
            onClick={downloadCSV}
            data-testid="tables-export-csv"
            className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] text-sm px-4 py-2 transition-colors"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        }
        testId="tables-header"
      />

      {/* Inventory KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="tables-inventory-kpis">
        <KPI label="Total" value={inv.total} accent="text-white" />
        <KPI label="Sold" value={inv.sold} accent="text-emerald-400" testId="kpi-sold" />
        <KPI label="Reserved" value={inv.reserved} accent="text-amber-300" testId="kpi-reserved" />
        <KPI label="Available" value={inv.available} accent="text-sky-300" testId="kpi-available" />
        <KPI label="Blocked" value={inv.blocked} accent="text-zinc-400" testId="kpi-blocked" />
      </section>

      {/* Revenue KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="tables-revenue-kpis">
        <KPI label="Potential" value={formatLakhs(rupeesToLakhs(rev.potential))} accent="text-zinc-100" />
        <KPI label="Actual" value={formatLakhs(rupeesToLakhs(rev.actual))} accent="text-emerald-400" />
        <KPI label="Collected" value={formatLakhs(rupeesToLakhs(rev.collected))} accent="text-emerald-300" />
        <KPI label="Pending" value={formatLakhs(rupeesToLakhs(rev.pending))} accent="text-amber-300" />
        <KPI label="Avg Selling" value={formatLakhs(rupeesToLakhs(rev.avg_selling_price))} accent="text-zinc-100" />
        <KPI label="Avg Discount" value={`${rev.avg_discount_pct.toFixed(1)}%`} accent={rev.avg_discount_pct > 15 ? "text-rose-300" : "text-zinc-100"} />
      </section>

      {/* Category breakdown + analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-5" testId="tables-category-breakdown">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Category breakdown</div>
              <h3 className="font-display text-lg mt-1">Inventory & revenue per category</h3>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline text-zinc-500">
                <th className="pb-2 font-normal">Cat</th>
                <th className="pb-2 font-normal text-right">Total</th>
                <th className="pb-2 font-normal text-right">Sold</th>
                <th className="pb-2 font-normal text-right">Available</th>
                <th className="pb-2 font-normal text-right">Bottles</th>
                <th className="pb-2 font-normal text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {stats.category_breakdown.map((c) => (
                <tr key={c.category} className="border-t border-zinc-900">
                  <td className="py-2.5 font-display text-base">{c.category}</td>
                  <td className="py-2.5 text-right text-zinc-300 tabular">{c.total}</td>
                  <td className="py-2.5 text-right text-emerald-400 tabular">{c.sold}</td>
                  <td className="py-2.5 text-right text-sky-300 tabular">{c.available}</td>
                  <td className="py-2.5 text-right text-zinc-300 tabular">{c.bottles}</td>
                  <td className="py-2.5 text-right text-zinc-100 tabular">{formatLakhs(rupeesToLakhs(c.actual_revenue))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="p-5" testId="tables-revenue-by-salesperson">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Sales team</div>
              <h3 className="font-display text-lg mt-1">Revenue by salesperson</h3>
            </div>
          </div>
          {stats.salespeople.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No tables sold yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, stats.salespeople.length * 48)}>
              <BarChart layout="vertical" data={stats.salespeople.map((s) => ({ ...s, revenue_lakhs: s.revenue / 100000 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#71717a", fontSize: 11 }} stroke="#27272a" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 12 }} width={100} stroke="#27272a" />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid #27272a", fontSize: 12 }}
                  formatter={(v) => [`₹ ${v.toFixed(2)} L`, "Revenue"]}
                />
                <Bar dataKey="revenue_lakhs" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Daily sales line */}
      {stats.daily_sales.length > 0 && (
        <Card className="p-5 mb-8" testId="tables-daily-sales">
          <div className="overline mb-1">Daily trend</div>
          <h3 className="font-display text-lg mb-4">Tables sold per day</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={stats.daily_sales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} stroke="#27272a" />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} stroke="#27272a" />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #27272a", fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 mb-3" testId="tables-filters">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-zinc-500 ml-1" />
          <Select value={filterCategory} onChange={setFilterCategory} options={[["all", "All categories"], ...categories.map((c) => [c, `Category ${c}`])]} testId="filter-category" />
          <Select value={filterStatus} onChange={setFilterStatus} options={[["all", "All statuses"], ...STATUS_OPTIONS.map((s) => [s.v, s.label])]} testId="filter-status" />
          <Select value={filterSoldBy} onChange={setFilterSoldBy} options={[["all", "All sellers"], ...salespeopleSet.map((s) => [s, s])]} testId="filter-soldby" />
          <Select value={filterPayment} onChange={setFilterPayment} options={[["all", "All payments"], ...PAYMENT_OPTIONS.map((p) => [p.v, p.label])]} testId="filter-payment" />
          <div className="relative ml-auto flex-1 min-w-[200px] max-w-[320px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" />
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search table #, customer, phone, company…"
              data-testid="tables-search"
              className="w-full pl-8 pr-3 py-1.5 bg-[#0a0a0a] border border-zinc-800 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="text-xs text-zinc-500 tabular">{filtered.length} of {tables.length}</div>
        </div>
      </Card>

      {/* Table grid */}
      <Card className="overflow-hidden" testId="tables-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-zinc-900">
                <th className="px-4 py-3 font-normal">Table</th>
                <th className="px-4 py-3 font-normal">Cat</th>
                <th className="px-4 py-3 font-normal text-right">List</th>
                <th className="px-4 py-3 font-normal text-right">Close</th>
                <th className="px-4 py-3 font-normal">Sold By</th>
                <th className="px-4 py-3 font-normal">Customer</th>
                <th className="px-4 py-3 font-normal text-right">Bottles</th>
                <th className="px-4 py-3 font-normal">Status</th>
                <th className="px-4 py-3 font-normal">Payment</th>
                <th className="px-4 py-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-zinc-900 hover:bg-[#161616] transition-colors cursor-pointer"
                  onClick={() => openEdit(t)}
                  data-testid={`table-row-${t.table_number}`}
                >
                  <td className="px-4 py-3 font-display font-semibold text-base">{t.table_number}</td>
                  <td className="px-4 py-3 text-zinc-400">{t.category}</td>
                  <td className="px-4 py-3 text-right tabular text-zinc-300">{formatLakhs(rupeesToLakhs(t.list_price))}</td>
                  <td className="px-4 py-3 text-right tabular">
                    {t.closing_price != null ? (
                      <span className={t.closing_price < t.list_price ? "text-amber-300" : "text-emerald-300"}>
                        {formatLakhs(rupeesToLakhs(t.closing_price))}
                      </span>
                    ) : <span className="text-zinc-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-300 truncate max-w-[120px]">{t.sold_by || <span className="text-zinc-700">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-200 truncate max-w-[160px]">
                    {t.customer_name ? (
                      <>
                        <div className="truncate">{t.customer_name}</div>
                        {t.company_name && <div className="text-[10px] text-zinc-500 truncate">{t.company_name}</div>}
                      </>
                    ) : <span className="text-zinc-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular text-zinc-300">
                    {(t.bottles || t.complimentary_bottles) ? (
                      <span>{t.bottles}{t.complimentary_bottles ? ` + ${t.complimentary_bottles}` : ""}</span>
                    ) : <span className="text-zinc-700">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={t.status} /></td>
                  <td className="px-4 py-3">
                    {t.status === "sold" ? <PaymentBadge value={t.payment_status} /> : <span className="text-zinc-700 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                      data-testid={`table-edit-${t.table_number}`}
                      className="text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 border border-emerald-500/40 hover:bg-emerald-500/10"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-zinc-500">No tables match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Side Panel */}
      {editing && (
        <EditPanel
          editing={editing}
          setEditing={setEditing}
          users={users}
          logEntries={logEntries}
          onSave={saveEdit}
          onClose={closeEdit}
        />
      )}
    </Layout>
  );
}

/* ============================================================ */

function KPI({ label, value, accent = "text-white", testId }) {
  return (
    <Card className="p-4" testId={testId}>
      <div className="overline mb-1.5">{label}</div>
      <div className={`font-display font-semibold text-2xl tabular ${accent}`}>{value}</div>
    </Card>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="px-2.5 py-1.5 bg-[#0a0a0a] border border-zinc-800 text-xs text-zinc-200 focus:border-emerald-500 focus:outline-none cursor-pointer"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Field({ label, optional, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block overline text-zinc-500 mb-1">
        {label}
        {optional && <span className="ml-1.5 normal-case tracking-normal text-[10px] text-zinc-700">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none transition-colors";

function EditPanel({ editing, setEditing, users, logEntries, onSave, onClose }) {
  const set = (k) => (e) => setEditing((x) => ({ ...x, [k]: e.target.value }));
  const setNum = (k) => (e) => setEditing((x) => ({ ...x, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const discount = editing.closing_price != null && editing.closing_price !== ""
    ? Number(editing.list_price) - Number(editing.closing_price)
    : 0;
  const discountPct = editing.list_price > 0 ? (discount / Number(editing.list_price) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="tables-edit-panel">
      <button onClick={onClose} className="flex-1 bg-black/70 backdrop-blur-sm" aria-label="Close" />
      <aside className="w-full max-w-[560px] bg-[#0a0a0a] border-l border-zinc-900 overflow-y-auto flex flex-col">
        <header className="px-6 py-5 border-b border-zinc-900 flex items-center justify-between sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <div className="overline text-zinc-500">Table {editing.table_number} · Category {editing.category}</div>
            <div className="font-display font-bold text-2xl mt-1">{formatLakhs(rupeesToLakhs(editing.list_price))} list</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#181818]" data-testid="tables-edit-close">
            <X className="h-5 w-5 text-zinc-400" />
          </button>
        </header>

        <div className="p-6 space-y-5 flex-1">
          {/* Status & Sale */}
          <section>
            <div className="overline text-zinc-500 mb-3">Sale details</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <select value={editing.status} onChange={set("status")} className={`${inputCls} cursor-pointer`} data-testid="edit-status">
                  {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Closing Price (₹)">
                <input type="number" value={editing.closing_price ?? ""} onChange={setNum("closing_price")} placeholder={String(editing.list_price)} className={inputCls} data-testid="edit-closing-price" />
              </Field>
              <Field label="Sold By">
                <select value={editing.sold_by || ""} onChange={set("sold_by")} className={`${inputCls} cursor-pointer`} data-testid="edit-sold-by">
                  <option value="">— Select —</option>
                  {users.map((u) => <option key={u.id} value={u.name}>{u.name} · {u.role}</option>)}
                </select>
              </Field>
              <Field label="Date Sold">
                <input
                  type="date"
                  value={editing.date_sold ? editing.date_sold.slice(0, 10) : ""}
                  onChange={(e) => setEditing((x) => ({ ...x, date_sold: e.target.value || null }))}
                  className={inputCls}
                  data-testid="edit-date-sold"
                />
              </Field>
            </div>
            {editing.closing_price && (
              <div className="mt-2 text-[11px] text-zinc-500">
                Discount: <span className={discountPct > 15 ? "text-rose-300" : "text-zinc-300"}>{formatLakhs(rupeesToLakhs(discount))} ({discountPct.toFixed(1)}%)</span>
              </div>
            )}
          </section>

          {/* Customer */}
          <section>
            <div className="overline text-zinc-500 mb-3">Customer</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" span={2}>
                <input value={editing.customer_name || ""} onChange={set("customer_name")} placeholder="Customer full name" className={inputCls} data-testid="edit-customer-name" />
              </Field>
              <Field label="Phone">
                <input value={editing.customer_phone || ""} onChange={set("customer_phone")} placeholder="+91…" className={inputCls} data-testid="edit-customer-phone" />
              </Field>
              <Field label="Email">
                <input value={editing.customer_email || ""} onChange={set("customer_email")} placeholder="email@…" className={inputCls} data-testid="edit-customer-email" />
              </Field>
              <Field label="Company" optional span={2}>
                <input value={editing.company_name || ""} onChange={set("company_name")} placeholder="Company / brand" className={inputCls} data-testid="edit-company" />
              </Field>
            </div>
          </section>

          {/* Bottles */}
          <section>
            <div className="overline text-zinc-500 mb-3 flex items-center gap-2"><Wine className="h-3 w-3" /> Bottles</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bottles Committed">
                <input type="number" min={0} value={editing.bottles || 0} onChange={setNum("bottles")} className={inputCls} data-testid="edit-bottles" />
              </Field>
              <Field label="Complimentary" optional>
                <input type="number" min={0} value={editing.complimentary_bottles || 0} onChange={setNum("complimentary_bottles")} className={inputCls} data-testid="edit-comp-bottles" />
              </Field>
            </div>
          </section>

          {/* Payment */}
          <section>
            <div className="overline text-zinc-500 mb-3 flex items-center gap-2"><IndianRupee className="h-3 w-3" /> Payment</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payment Status">
                <select value={editing.payment_status} onChange={set("payment_status")} className={`${inputCls} cursor-pointer`} data-testid="edit-payment-status">
                  {PAYMENT_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Amount Received (₹)">
                <input type="number" min={0} value={editing.amount_received || 0} onChange={setNum("amount_received")} className={inputCls} data-testid="edit-amount-received" />
              </Field>
              <Field label="Payment Due">
                <input
                  type="date"
                  value={editing.payment_due_date ? editing.payment_due_date.slice(0, 10) : ""}
                  onChange={(e) => setEditing((x) => ({ ...x, payment_due_date: e.target.value || null }))}
                  className={inputCls}
                  data-testid="edit-payment-due"
                />
              </Field>
              <div className="text-xs text-zinc-500 self-end pb-2 tabular">
                Pending: {formatLakhs(rupeesToLakhs(Math.max(0, (Number(editing.closing_price) || 0) - (Number(editing.amount_received) || 0))))}
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <div className="overline text-zinc-500 mb-3">Internal notes</div>
            <textarea
              value={editing.notes || ""}
              onChange={set("notes")}
              rows={3}
              placeholder="e.g. Celebrity guest, wants premium parking, balance by Dec 15…"
              className={`${inputCls} resize-none`}
              data-testid="edit-notes"
            />
          </section>

          {/* Activity log */}
          <section>
            <div className="overline text-zinc-500 mb-3 flex items-center gap-2"><History className="h-3 w-3" /> Activity log</div>
            {logEntries.length === 0 ? (
              <div className="text-xs text-zinc-600">No changes recorded yet.</div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {logEntries.map((e) => (
                  <div key={e.id} className="text-[11px] border-l-2 border-zinc-800 pl-3 py-1">
                    <div className="flex items-baseline justify-between">
                      <span className="text-zinc-300">{e.actor_name || e.actor_email}</span>
                      <span className="text-zinc-600 tabular">{formatDateShort(e.timestamp)}</span>
                    </div>
                    <div className="text-zinc-500 mt-0.5 space-y-0.5">
                      {e.changes.map((c, i) => (
                        <div key={i}>
                          <span className="text-zinc-400">{c.field}</span>:&nbsp;
                          <span className="text-zinc-600 line-through">{String(c.from ?? "—").slice(0, 30)}</span>
                          &nbsp;→&nbsp;
                          <span className="text-zinc-200">{String(c.to ?? "—").slice(0, 30)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <footer className="px-6 py-4 border-t border-zinc-900 flex items-center gap-3 sticky bottom-0 bg-[#0a0a0a]">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm text-zinc-300 hover:bg-[#181818] border border-zinc-800"
            data-testid="edit-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            data-testid="edit-save"
            className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium transition-colors"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </footer>
      </aside>
    </div>
  );
}
